/**
 * configDirty — canonical dirty-detection for the cloud Device setup save model (#86bbm58f7).
 *
 * THE MODEL (David 2026-08-25): two states — `pristine` (the config as loaded) and `form` (the working
 * config with the user's staged changes). Dirty = the working config differs FROM the original; Revert =
 * restore the original. No manufactured revert, no shadow state.
 *
 * THE ONE WRINKLE this file exists to fix: the setup UI "boils the lake" — it shows every field the engine
 * supports, seeded to its default even when the stored blob omits the key. When a master edits one of those
 * seeded-absent fields (Advanced overlay `patchConn`, POS `patchPos`) the patcher WRITES the key into
 * `form.config`. Toggling it back to the default then leaves `key: '<default>'` where `pristine` has no key
 * at all — a PHANTOM diff (identical effect, different bytes). A plain deep-equal (`formsEqual`) never
 * re-equals after such a round-trip, so Revert/Save stick on though nothing effectively changed.
 *
 * FIX: compare the EFFECTIVE config — fold the parsed device edits (`cfg`) back into the blob, then drop
 * every connector key whose value equals its schema default (absent ≡ default) and any section left empty
 * by that. Device sections need no pruning: `writeDevice` is present-only and runs identically on both
 * sides, so they only differ where a value genuinely differs. The result is the same canonical basis cloudapi uses
 * server-side (strip + deep-compare) and the natural basis for the "Apply Changes to Device" diff.
 */

import { detectFamily, writeDevice, readConnector, connectorFields, deviceFields, boolFromCfg, hasConnectorBlock } from './configParser';
import { formsEqual } from './setupCommon';
import type17 from './pos-types/type17.json';
import type19 from './pos-types/type19.json';
import type20 from './pos-types/type20.json';
import type21 from './pos-types/type21.json';

const POS_TYPES = { 17: type17, 19: type19, 20: type20, 21: type21 };

// A connector value is always a string in config.files.connector; booleans serialize to '0'/'1'.
// (Mirrors CloudDeviceSetup.posValStr — kept local so this module is pure/standalone-testable.)
const posValStr = (v) => (v === true ? '1' : v === false ? '0' : String(v));

// Which store a POS field's value lives in: 'scale' → [scaleN], else [posN]. (Mirrors posFieldStore.)
const fieldStore = (schema, key) => {
  const field = schema && (schema.fields || []).find((f) => f.key === key);
  if (!field) return 'pos';
  const section = schema.sections && schema.sections.find((s) => s.id === field.section);
  return (section && section.store) || 'pos';
};

// Connector namespace sections that carry a FIXED schema (global/rpc/dragon/cloud). A posN/scaleN is
// dynamic and handled separately; its presence can be meaningful (scaleN present ⟺ scale sensing on), so
// those are never dropped even when empty.
const isFixedConnSection = (name) => !/^(pos|scale)\d+$/i.test(name);

/**
 * Canonicalize a config blob so "absent" and "present-at-default" compare equal. Immutable — returns a
 * normalized deep copy; only the connector namespace is touched (device sections are symmetric already).
 * @param {Object} config - a full parsed config blob (INI flat or modern JSON wrapper)
 * @returns {Object} normalized copy
 */
export const canonConfig = (config) => {
  if (!config || typeof config !== 'object') return config;
  const c = JSON.parse(JSON.stringify(config));
  const conn = c.files && c.files.connector;
  if (!conn || typeof conn !== 'object') return c;

  // 1. Fixed connector sections: drop keys equal to their schema default. Bool fields compare via the
  //    app's boolean equivalence class (boolFromCfg) so a stored 'off'/'on'/'true'/'false' equals the
  //    '0'/'1' default form — otherwise a seeded toggle round-trip leaves a PHANTOM diff ('off' vs absent)
  //    and the form sticks dirty forever. (#86bbm58f7)
  for (const f of connectorFields()) {
    const sec = conn[f.section];
    if (!sec || typeof sec !== 'object' || !Object.prototype.hasOwnProperty.call(sec, f.key)) continue;
    const atDefault = f.kind === 'bool'
      ? boolFromCfg(sec[f.key]) === boolFromCfg(f.def)
      : String(sec[f.key]) === String(f.def);
    if (atDefault) delete sec[f.key];
  }

  // 2. POS lanes: drop each posN/scaleN key equal to its pos-type schema default. `type` is kept (it
  //    identifies the lane). A scaleN is left in place even when emptied — its presence means sensing-on.
  for (const secName of Object.keys(conn)) {
    const m = /^pos(\d+)$/i.exec(secName);
    if (!m) continue;
    const posSec = conn[secName];
    const schema = POS_TYPES[Number(posSec && posSec.type)];
    if (!schema) continue;
    const scaleSec = conn['scale' + m[1]];
    for (const field of (schema.fields || [])) {
      const target = fieldStore(schema, field.key) === 'scale' ? scaleSec : posSec;
      if (!target || typeof target !== 'object' || !Object.prototype.hasOwnProperty.call(target, field.key)) continue;
      // Prune against the READ fallback, not the seed default (#86bc2p0fh). This whole pass exists to
      // make "absent" and "explicitly at the default" one equivalence class — and the class that is
      // TRUE on the device is the one the connector applies to a missing key, which `storedDefault`
      // names when it differs from what a NEW lane is seeded with. ticket_source is exactly that:
      // seeded commandcloud, but connector_config.py reads an absent key as apex. Pruning against the
      // SEED made writing commandcloud onto a fielded lane with no stored key cancel itself out —
      // canon said absent ≡ commandcloud — so the device never went dirty, no Push button appeared,
      // and the operator could not switch that lane to Command Cloud at all.
      const readDefault = field.storedDefault !== undefined ? field.storedDefault : field.default;
      const atDefault = field.kind === 'bool'
        ? boolFromCfg(target[field.key]) === boolFromCfg(readDefault)
        : String(target[field.key]) === posValStr(readDefault);
      if (atDefault) delete target[field.key];
    }
  }

  // 3. Drop fixed connector sections left empty by the pruning above (empty ≡ absent for those).
  for (const secName of Object.keys(conn)) {
    if (isFixedConnSection(secName) && conn[secName] && typeof conn[secName] === 'object'
        && Object.keys(conn[secName]).length === 0) {
      delete conn[secName];
    }
  }

  return c;
};

/**
 * The config that WOULD be saved for a form: fold the parsed device edits (`cfg`) back into the blob via
 * writeDevice (connector edits already live in form.config). An Unknown/empty device (cfg=null) uses the
 * blob unchanged.
 */
export const effectiveConfig = (form) =>
  form && form.cfg ? writeDevice(form.config, detectFamily(form.config), form.cfg) : (form && form.config);

/**
 * Is the working form effectively different from its pristine snapshot? Compares org-assignment scalars
 * plus the canonicalized effective config (absent ≡ default). This is THE dirty gate for Save/Apply —
 * enabled only when canon(form) !== canon(pristine); a hand-revert (retype the original) clears it.
 */
export const isDeviceDirty = (form, pristine) => {
  if (form == null || pristine == null) return false;
  if (form.name !== pristine.name) return true;
  if (form.customerId !== pristine.customerId) return true;
  if (form.projectId !== pristine.projectId) return true;
  return !formsEqual(canonConfig(effectiveConfig(form)), canonConfig(effectiveConfig(pristine)));
};

/**
 * Do two raw config blobs canonicalize equal (absent ≡ default)? Used by the Advanced overlay to decide
 * whether closing it needs a KEEP/DISCARD prompt — a seeded field toggled on→off within the overlay is
 * NOT a change. (#86bbm58f7 piece 2)
 */
export const canonConfigEqual = (a, b) => formsEqual(canonConfig(a), canonConfig(b));

// --- Change list for the "Push Changes to Device" review (#86bbm58f7 piece 4) --------------------------
// A human-readable diff of what a push will send, so the user can confirm they didn't change something
// unintended. Labels are schema-resolved where known, else "section · key". Camera sections are handled
// as whole operations below so credentials embedded in RTSP URLs never leak into a generic field row.

const fmtVal = (v) => (v == null || v === '' ? '—' : String(v));
const isCamSection = (name) => /^cam(era)?\d+$/i.test(name);
const safeStreamUrl = (v) => fmtVal(v && String(v).replace(/(:\/\/)[^/@]*@/, '$1'));

// Flatten a config's editable sections to { 'file.section.key': value }. Modern JSON exposes files.dragon
// + files.connector; a flat INI (legacy) is one pseudo-file '_flat'.
const flattenConfig = (config) => {
  const out = {};
  if (!config || typeof config !== 'object') return out;
  const addFile = (file, sections) => {
    for (const section of Object.keys(sections || {})) {
      if (isCamSection(section)) continue;
      const sec = sections[section];
      if (sec && typeof sec === 'object') {
        for (const key of Object.keys(sec)) out[`${file}.${section}.${key}`] = sec[key];
      }
    }
  };
  if (config.files) { addFile('dragon', config.files.dragon); addFile('connector', config.files.connector); }
  else addFile('_flat', config);
  return out;
};

const connLabel = (section, key) => {
  const f = connectorFields().find((d) => d.section === section && d.key === key);
  return f && (f.label || f.form);
};
const devLabel = (family, section, key) => {
  const f = deviceFields(family).find((d) => d.section === section && d.key === key);
  return f && (f.label || f.form);
};
const posLabel = (posSec, key) => {
  const schema = POS_TYPES[Number(posSec && posSec.type)];
  const f = schema && (schema.fields || []).find((x) => x.key === key);
  const nm = (posSec && posSec.name) || 'POS';
  return f ? `${nm}: ${f.label}` : `${nm}: ${key}`;
};

/**
 * The human-readable change list a "Push Changes to Device" will send: [{ path, label, from, to }].
 * Compares the canonical effective config (absent ≡ default) of pristine vs form, plus the device Name.
 * (Customer/Site changes are labelled by the caller, which has the id→name maps.)
 *
 * POS lanes are collapsed: a whole-lane ADD or DELETE is ONE row ("POS source added/removed: <type>")
 * rather than one row per field — posN.type is the lane discriminator, not a field, so it is never shown
 * alone (it collides with a schema field also keyed `type`). A pruned at-default value shows its real
 * schema default as the "from" (not "—", which implied the field was blank), and boolean fields render
 * On/Off (the config stores them numeric 0/1). (#86bbm58f7 review-sheet polish)
 */
// Product names for the posN.type lane discriminator — the schema `.fields` carry no lane-level name.
const POS_TYPE_NAMES = { 17: 'LPR', 19: 'Scale Watcher', 20: 'Facility Tracker', 21: 'PSCL' };
const posTypeName = (t) => POS_TYPE_NAMES[Number(t)] || `Type ${t}`;
const laneNumOf = (section) => { const m = /^(?:pos|scale)(\d+)$/i.exec(section); return m ? m[1] : null; };

// The schema default for a connector path, so a review "from"/"to" can show the real default value when
// canonConfig pruned the at-default key on one side (else it reads "—", implying the field was blank).
// This MUST resolve to the same value canonConfig pruned against — the READ fallback (storedDefault),
// not the seed default — or the side canon pruned renders as the wrong value. With them out of step a
// genuine commandcloud->apex switch rendered "commandcloud -> commandcloud": the footer said one change
// and the sheet affirmatively reported the wrong one. (#86bc2p0fh)
const connDefaultFor = (section, key, connForType) => {
  const lane = /^(?:pos|scale)(\d+)$/i.exec(section);
  if (lane) {
    const posSec = connForType['pos' + lane[1]];
    const schema = POS_TYPES[Number(posSec && posSec.type)];
    const f = schema && (schema.fields || []).find((x) => x.key === key);
    if (!f) return undefined;
    return posValStr(f.storedDefault !== undefined ? f.storedDefault : f.default);
  }
  const f = connectorFields().find((d) => d.section === section && d.key === key);
  return f ? f.def : undefined;
};

// The schema `kind` for a flattened path (drives On/Off rendering for boolean fields).
const fieldKind = (file, section, key, family, connForType) => {
  if (file === 'connector') {
    const lane = /^(?:pos|scale)(\d+)$/i.exec(section);
    if (lane) {
      const posSec = connForType['pos' + lane[1]];
      const schema = POS_TYPES[Number(posSec && posSec.type)];
      const f = schema && (schema.fields || []).find((x) => x.key === key);
      return f && f.kind;
    }
    const f = connectorFields().find((d) => d.section === section && d.key === key);
    return f && f.kind;
  }
  const f = deviceFields(family).find((d) => d.section === section && d.key === key);
  return f && f.kind;
};

// Render a value for a review row. On/Off is used ONLY when the schema tells us the field is a bool
// (kind === 'bool'). An unknown/unschema'd field renders its raw value verbatim — a numeric value shows
// as a NUMBER, never guessed into a bool from a 0/1: the device config carries no type, so with no schema
// kind we don't infer one (render the number now; add a schema entry later). (#86bbm58f7, David 2026-08-26)
const fmtField = (kind, v) => {
  if (v == null || v === '') return '—';
  if (kind === 'bool') return boolFromCfg(v) ? 'On' : 'Off';
  return String(v);
};

export const configChanges = (pristine, form) => {
  if (!pristine || !form) return [];
  const changes = [];
  if (form.name !== pristine.name) {
    changes.push({ path: 'name', label: 'Name', from: fmtVal(pristine.name), to: fmtVal(form.name) });
  }
  const family = detectFamily((form.config) || {});
  const pf = flattenConfig(canonConfig(effectiveConfig(pristine)));
  const ff = flattenConfig(canonConfig(effectiveConfig(form)));
  const pConn = (pristine.config && pristine.config.files && pristine.config.files.connector) || {};
  const fConn = (form.config && form.config.files && form.config.files.connector) || {};

  // Wholesale connector-block add/remove is ONE change (#86bc2p0fh). "Enable POS" adds the entire
  // files.connector block (global/rpc/dragon/cloud scaffolding) and "Disable POS" removes it — many leaf
  // fields, but a SINGLE logical action. Collapse it to one row (like a whole POS lane) and skip the
  // per-field connector rows below, so the footer reads "Push 1 Change", not "5"/"8". Uses the SAME
  // block-presence helper as the Enable/Disable toggle (hasConnectorBlock — a non-empty block), so an
  // empty {} on one side reads consistently as "no block" instead of a raw-truthiness mismatch.
  const pHasConn = hasConnectorBlock(pristine.config);
  const fHasConn = hasConnectorBlock(form.config);
  const connBlockWholesale = pHasConn !== fHasConn;
  if (connBlockWholesale) {
    changes.push(fHasConn
      ? { path: 'connector', label: 'POS enabled', from: '—', to: 'Connector added' }
      : { path: 'connector', label: 'POS disabled', from: 'Connector removed', to: '—' });
  }

  // Partition every changed path: connector POS/scale lanes (grouped by lane number) vs everything else.
  const laneNums = new Set();
  const flatPaths = [];
  for (const path of new Set([...Object.keys(pf), ...Object.keys(ff)])) {
    const [file, section] = path.split('.');
    // Collapsed into the single block row above — EVERYTHING under files.connector, deliberately.
    // Adding or removing the connector block is ONE change no matter how many leaves or lanes move
    // with it (#86bc2p0fh, David's requirement). That includes a POS lane added in the same batch and
    // any scaffolding field tuned in Advanced before the first push: the block did not exist a moment
    // ago, so nothing is being silently overwritten, the operator picked that lane's type in a sheet
    // seconds earlier, and reverting the single row correctly drops the whole block, lane and tuning
    // included. Do NOT carve lanes back out — a review pass proposed exactly that and it reintroduces
    // the "Push 5 Changes" noise this row exists to remove.
    if (connBlockWholesale && file === 'connector') continue;
    const n = file === 'connector' ? laneNumOf(section) : null;
    if (n != null) laneNums.add(n);
    else flatPaths.push(path);
  }

  // Device + connector-fixed (global/rpc/dragon/cloud) field changes.
  for (const path of flatPaths) {
    const a = pf[path], b = ff[path];
    if (String(a == null ? '' : a) === String(b == null ? '' : b)) continue;
    const [file, section, key] = path.split('.');
    const kind = fieldKind(file, section, key, family, fConn);
    const label = file === 'connector'
      ? (connLabel(section, key) || `${section} · ${key}`)
      : (devLabel(family, section, key) || `${section} · ${key}`);
    const from = a != null ? a : (file === 'connector' ? connDefaultFor(section, key, pConn) : undefined);
    const to = b != null ? b : (file === 'connector' ? connDefaultFor(section, key, fConn) : undefined);
    changes.push({ path, label, from: fmtField(kind, from), to: fmtField(kind, to) });
  }

  // POS lanes: one row for a whole-lane add/delete; per-field rows for an edited lane (skip discriminator).
  for (const n of [...laneNums].sort((x, y) => Number(x) - Number(y))) {
    const laneHas = (map) => Object.keys(map).some((p) => laneNumOf(p.split('.')[1]) === n);
    const inP = laneHas(pf), inF = laneHas(ff);
    const desc = (conn) => {
      const sec = conn['pos' + n] || {};
      const tn = posTypeName(sec.type);
      return sec.name && sec.name !== tn ? `${tn} "${sec.name}"` : tn;
    };
    if (inF && !inP) { changes.push({ path: `connector.pos${n}`, label: 'POS source added', from: '—', to: desc(fConn) }); continue; }
    if (inP && !inF) { changes.push({ path: `connector.pos${n}`, label: 'POS source removed', from: desc(pConn), to: '—' }); continue; }
    // Edited lane — per-field, skipping the posN.type discriminator (it mislabels as a schema field).
    const posSec = fConn['pos' + n] || pConn['pos' + n] || {};
    for (const path of new Set([...Object.keys(pf), ...Object.keys(ff)])) {
      const [file, section, key] = path.split('.');
      if (file !== 'connector' || laneNumOf(section) !== n) continue;
      if (/^pos\d+$/i.test(section) && key === 'type') continue;
      const a = pf[path], b = ff[path];
      if (String(a == null ? '' : a) === String(b == null ? '' : b)) continue;
      const kind = fieldKind(file, section, key, family, fConn);
      const from = a != null ? a : connDefaultFor(section, key, pConn);
      const to = b != null ? b : connDefaultFor(section, key, fConn);
      changes.push({ path, label: posLabel(posSec, key), from: fmtField(kind, from), to: fmtField(kind, to) });
    }
  }

  // Camera sections: discovery can add/remove a whole section or replace an existing section's stream.
  // Report each operation as ONE row. Never show embedded RTSP credentials in the review. (#86bbm53r2)
  const engOf = (config) => ((config && config.files) ? (config.files.dragon || {}) : (config || {}));
  const camSecsOf = (config) => Object.keys(engOf(config)).filter(isCamSection);
  const pCams = camSecsOf(pristine.config);
  const fCams = camSecsOf(form.config);
  const camNum = (s) => parseInt(String(s).replace(/\D/g, ''), 10) || 0;
  for (const sec of [...new Set([...pCams, ...fCams])].sort((a, b) => camNum(a) - camNum(b))) {
    const inP = pCams.includes(sec);
    const inF = fCams.includes(sec);
    if (inF && !inP) {
      const s = engOf(form.config)[sec] || {};
      const nm = (s.name != null && String(s.name).trim()) || sec;
      changes.push({ path: `camera.${sec}`, label: 'Camera added', from: '—', to: nm });
    } else if (inP && !inF) {
      const s = engOf(pristine.config)[sec] || {};
      const nm = (s.name != null && String(s.name).trim()) || sec;
      changes.push({ path: `camera.${sec}`, label: 'Camera removed', from: nm, to: '—' });
    } else if (inP && inF) {
      const pSec = engOf(pristine.config)[sec] || {};
      const fSec = engOf(form.config)[sec] || {};
      if (!formsEqual(pSec, fSec)) {
        const recKey = family.urlKeys[0];
        const streamKeys = new Set([...family.urlKeys, 'make', 'model']);
        const nonStreamChanged = [...new Set([...Object.keys(pSec), ...Object.keys(fSec)])]
          .some((key) => !streamKeys.has(key)
            && String(pSec[key] == null ? '' : pSec[key]) !== String(fSec[key] == null ? '' : fSec[key]));
        if (nonStreamChanged) {
          const identity = (camera, fallback) =>
            (camera.name != null && String(camera.name).trim()) || fallback;
          changes.push({
            path: `camera.${sec}.replace`,
            label: 'Camera replaced',
            from: identity(pSec, sec),
            to: identity(fSec, sec),
          });
        } else {
          changes.push({
            path: `camera.${sec}.stream`,
            label: 'Camera stream changed',
            from: safeStreamUrl(pSec[recKey]),
            to: safeStreamUrl(fSec[recKey]),
          });
        }
      }
    }
  }
  return changes;
};

/**
 * Revert ONE staged change back to its pristine value, addressed by the `path` a configChanges row carries
 * — so the review sheet can drop a single unintended edit without discarding the whole batch. Immutable:
 * returns a NEW form. (#86bbm58f7 per-row revert / editable diff)
 *   - 'name' | 'customerId' | 'projectId' → reset the org scalar
 *   - 'dragon|_flat.<section>.<key>'      → reset a parsed field or raw unmanaged engine key
 *   - 'connector.<section>.<key>'         → reset that connector value in form.config (delete if pristine lacked it)
 *   - 'connector.posN' (no key)           → whole-lane add/remove: drop the lane, or restore it from pristine
 *   - 'connector' (no section)            → whole connector-block add/remove: restore or drop the block (#86bc2p0fh)
 *   - 'camera.camN[.stream]'              → restore the whole camera section from pristine
 */
export const revertChange = (form, pristine, path) => {
  if (!form || !pristine || !path) return form;
  if (path === 'name') return { ...form, name: pristine.name };
  if (path === 'customerId') return { ...form, customerId: pristine.customerId };
  if (path === 'projectId') return { ...form, projectId: pristine.projectId };
  const [file, section, key] = path.split('.');
  if (file === 'dragon' || file === '_flat') {
    const d = deviceFields(detectFamily(form.config)).find((x) => x.section === section && x.key === key);
    if (d) return { ...form, cfg: { ...form.cfg, [d.form]: pristine.cfg ? pristine.cfg[d.form] : undefined } };

    // Unmanaged engine keys live directly in the config blob. Restore the pristine value, or remove a
    // key that was added in this draft. Modern configs use files.dragon; legacy configs are flat.
    const nextConfig = JSON.parse(JSON.stringify(form.config || {}));
    const formEngine = file === 'dragon'
      ? ((nextConfig.files || (nextConfig.files = {})).dragon || (nextConfig.files.dragon = {}))
      : nextConfig;
    const pristineEngine = file === 'dragon'
      ? ((pristine.config && pristine.config.files && pristine.config.files.dragon) || {})
      : (pristine.config || {});
    const pristineSection = pristineEngine[section] || {};
    if (Object.prototype.hasOwnProperty.call(pristineSection, key)) {
      formEngine[section] = { ...(formEngine[section] || {}), [key]: pristineSection[key] };
    } else if (formEngine[section]) {
      const nextSection = { ...formEngine[section] };
      delete nextSection[key];
      formEngine[section] = nextSection;
    }
    return { ...form, config: nextConfig };
  }
  if (file === 'connector') {
    const nextConfig = JSON.parse(JSON.stringify(form.config || {}));
    if (!nextConfig.files) nextConfig.files = {};
    const pConnRaw = pristine.config && pristine.config.files && pristine.config.files.connector;
    if (!section) {
      // whole connector block add/remove (#86bc2p0fh, path 'connector'): restore pristine's state —
      // pristine HAD the block → restore it (revert a Disable POS); pristine LACKED it → drop it
      // (revert an Enable POS). Mirrors the single collapsed row configChanges emits.
      if (pConnRaw) nextConfig.files.connector = JSON.parse(JSON.stringify(pConnRaw));
      else delete nextConfig.files.connector;
      return { ...form, config: nextConfig, conn: readConnector(nextConfig) };
    }
    const fConn = nextConfig.files.connector || (nextConfig.files.connector = {});
    const pConn = pConnRaw || {};
    if (!key) {
      // whole POS lane add/remove — posN and its paired scaleN move together
      const n = section.replace(/\D/g, '');
      for (const s of ['pos' + n, 'scale' + n]) {
        if (pConn[s]) fConn[s] = JSON.parse(JSON.stringify(pConn[s]));   // pristine had it → restore (revert a delete)
        else delete fConn[s];                                           // pristine lacked it → drop (revert an add)
      }
    } else {
      const pSec = pConn[section] || {};
      if (Object.prototype.hasOwnProperty.call(pSec, key)) {
        fConn[section] = { ...(fConn[section] || {}), [key]: pSec[key] };
      } else if (fConn[section]) {
        const s2 = { ...fConn[section] };
        delete s2[key];
        fConn[section] = s2;
      }
    }
    return { ...form, config: nextConfig, conn: readConnector(nextConfig) };
  }
  if (file === 'camera') {
    // Whole camN add/remove (discovered-camera row): revert an add by dropping the section, or revert a
    // remove by restoring pristine's section. (#86bbm53r2)
    const nextConfig = JSON.parse(JSON.stringify(form.config || {}));
    const eng = nextConfig.files ? (nextConfig.files.dragon || (nextConfig.files.dragon = {})) : nextConfig;
    const pEng = (pristine.config && pristine.config.files) ? (pristine.config.files.dragon || {}) : (pristine.config || {});
    if (pEng[section]) eng[section] = JSON.parse(JSON.stringify(pEng[section]));
    else delete eng[section];
    return { ...form, config: nextConfig };
  }
  return form;
};
